import {
  BarChart3,
  Bell,
  Blocks,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Cog,
  FileSearch,
  HelpCircle,
  Home,
  LogOut,
  MapPin,
  QrCode,
  Settings,
  Settings2,
  Sun,
  Users,
  Wrench,
} from "@tamagui/lucide-icons";
import { useAuth, useUserRole, UserRole } from "common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  YStack,
  Image,
  Separator,
  Sheet,
  useMedia,
  Heading,
  XStack,
  SizableText,
  Button,
  Spinner,
  ScrollView,
  View,
} from "tamagui";

import { Navigation } from "./Navigation";
import homeServiceLogoImage from "../assets/images/home-service-logo.svg";
import logoImage from "../assets/images/logo.svg";
import { useAppSync } from "../contexts/AppSyncContext";
import { setDocumentTitle } from "../utils/setDocumentTitle";

export const Sidebar = ({
  isOpen,
  onOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) => {
  const media = useMedia();
  const isSmallScreen = media.sm;

  if (!isSmallScreen) {
    return (
      <ScrollView
        minWidth={300}
        maxWidth={300}
        minHeight="100%"
        backgroundColor="$gray2"
        padding="$3.5"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SidebarContent />
      </ScrollView>
    );
  }

  return (
    <Sheet
      modal
      open={isOpen}
      onOpenChange={onOpenChange}
      snapPoints={[80]}
      position={0}
      dismissOnSnapToBottom
    >
      <Sheet.Overlay />
      <Sheet.Frame padding="$3.5">
        <SidebarContent />
      </Sheet.Frame>
    </Sheet>
  );
};

const SidebarContent = () => {
  const { role } = useUserRole();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hubs, hubsLoading, selectedHub, selectHubById } = useAppSync();

  const [isHubSelectorOpen, setIsHubSelectorOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isCustomerView = role === UserRole.USER;
  const isInstallerView =
    role === UserRole.INSTALLER || role === UserRole.ADMIN;

  const onLogOutPress = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error(`logout failure, error: ${JSON.stringify(error)}`);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    setDocumentTitle({ hubName: selectedHub?.name });
  }, [selectedHub]);

  return (
    <YStack gap="$4" flex={1}>
      <Image
        source={{ uri: logoImage, width: 70, height: 70 }}
        width="100%"
        height={70}
        resizeMode="stretch"
        alt={t("sidebar.logoAlt")}
        alignSelf="center"
      />
      {isCustomerView && (
        <YStack gap="$2.5">
          {hubsLoading && <Spinner size="large" />}
          {!hubsLoading && !selectedHub && (
            <SizableText size="$3" color="$white10">
              {t("sidebar.noHubs")}
            </SizableText>
          )}
          {selectedHub && (
            <YStack
              gap="$2.5"
              cursor="pointer"
              onPress={() => setIsHubSelectorOpen(!isHubSelectorOpen)}
            >
              <XStack gap="$2" alignItems="center">
                <Heading fontSize="$4" lineHeight="$2" alignItems="center">
                  {selectedHub.name}
                </Heading>
                {isHubSelectorOpen ? <ChevronUp /> : <ChevronDown />}
              </XStack>
              <XStack gap="$2" alignItems="center">
                <MapPin size="$1" color="$white10" />
                <SizableText size="$3" color="$white10">
                  {selectedHub.location}
                </SizableText>
              </XStack>
            </YStack>
          )}
          {isHubSelectorOpen && (
            <YStack
              backgroundColor="$gray4"
              borderWidth={1}
              borderColor="$transparentBorder"
              borderRadius="$5"
              overflow="hidden"
            >
              {hubs.map(({ id, name }, index) => {
                const isLast = index === hubs.length - 1;
                const isSelected = selectedHub && id === selectedHub.id;
                const roomCount = 6; // TODO: fetch from the backend
                const deviceCount = 24; // TODO: fetch from the backend
                return (
                  <Button
                    key={id}
                    unstyled
                    flexDirection="row"
                    gap="$1.5"
                    alignItems="center"
                    justifyContent="space-between"
                    cursor="pointer"
                    padding="$3"
                    backgroundColor="$gray4"
                    hoverStyle={{ backgroundColor: "$gray6" }}
                    borderWidth={0}
                    borderColor="$transparentBorder"
                    borderBottomWidth={isLast ? 0 : 1}
                    onPress={() => selectHubById(id)}
                  >
                    <YStack gap="$1.5" alignItems="flex-start">
                      <SizableText size="$4" color="$white12">
                        {name}
                      </SizableText>
                      <SizableText size="$3" color="$white10">
                        {t("sidebar.roomsAndDevices", {
                          roomCount,
                          deviceCount,
                        })}
                      </SizableText>
                    </YStack>
                    {isSelected ? (
                      <CheckCircle2 color="$hgPrimary" />
                    ) : (
                      <Circle color="$gray8" />
                    )}
                  </Button>
                );
              })}
            </YStack>
          )}
        </YStack>
      )}

      <Separator />

      {!isCustomerView && !isInstallerView && <Spinner size="large" />}

      <Navigation>
        {isCustomerView ? (
          <>
            <Navigation.Button icon={Home} routePath="/dashboard">
              {t("sidebar.dashboard")}
            </Navigation.Button>
            <Navigation.Button icon={Sun} routePath="/weather">
              {t("sidebar.weather")}
            </Navigation.Button>
            <Navigation.Button icon={BarChart3} routePath="/analytics">
              {t("sidebar.analytics")}
            </Navigation.Button>
            <Navigation.DropdownMenu
              trigger={
                <Navigation.Button icon={Settings}>
                  {t("sidebar.settings.settings")}
                </Navigation.Button>
              }
            >
              <Navigation.Button icon={Settings2} routePath="/settings">
                {t("sidebar.settings.general")}
              </Navigation.Button>
              <Navigation.Button icon={Cog} routePath="/service-contact">
                {t("sidebar.settings.serviceContact")}
              </Navigation.Button>
              <Navigation.Button icon={FileSearch} routePath="/sensor-manuals">
                {t("sidebar.settings.sensorManuals")}
              </Navigation.Button>
              <Navigation.Button icon={HelpCircle} routePath="/help">
                {t("sidebar.settings.help")}
              </Navigation.Button>
              <Navigation.Button
                icon={QrCode}
                // routePath="/two-factor-authentication"
              >
                {t("sidebar.settings.twoFactorAuthentication")}
              </Navigation.Button>
              <Navigation.Button
                icon={LogOut}
                isLoading={isLoggingOut}
                onPress={onLogOutPress}
              >
                {t("sidebar.settings.logout")}
              </Navigation.Button>
            </Navigation.DropdownMenu>
          </>
        ) : isInstallerView ? (
          <>
            <Navigation.Button icon={Bell} routePath="/alerts">
              {t("sidebar.alerts")}
            </Navigation.Button>
            <Navigation.Button icon={Blocks} routePath="/base-stations">
              {t("sidebar.baseStations")}
            </Navigation.Button>
            <Navigation.Button icon={Wrench} routePath="/service-requests">
              {t("sidebar.serviceRequests")}
            </Navigation.Button>
            <Navigation.Button icon={Users} routePath="/customers">
              {t("sidebar.customers")}
            </Navigation.Button>
            <Navigation.Button icon={BarChart3} routePath="/analytics">
              {t("sidebar.analytics")}
            </Navigation.Button>
            <Navigation.DropdownMenu
              trigger={
                <Navigation.Button icon={Settings}>
                  {t("sidebar.settings.settings")}
                </Navigation.Button>
              }
            >
              <Navigation.Button icon={Settings2} routePath="/settings">
                {t("sidebar.settings.general")}
              </Navigation.Button>
              <Navigation.Button icon={Blocks} routePath="/integrations">
                {t("sidebar.settings.integrations")}
              </Navigation.Button>
              <Navigation.Button icon={Users}>
                {t("sidebar.settings.userManagement")}
              </Navigation.Button>
              <Navigation.Button icon={Cog} routePath="/service-contact">
                {t("sidebar.settings.serviceContact")}
              </Navigation.Button>
              <Navigation.Button icon={FileSearch} routePath="/sensor-manuals">
                {t("sidebar.settings.sensorManuals")}
              </Navigation.Button>
              <Navigation.Button icon={HelpCircle} routePath="/help">
                {t("sidebar.settings.help")}
              </Navigation.Button>
              <Navigation.Button
                icon={QrCode}
                // routePath="/two-factor-authentication"
              >
                {t("sidebar.settings.twoFactorAuthentication")}
              </Navigation.Button>
              <Navigation.Button
                icon={LogOut}
                isLoading={isLoggingOut}
                onPress={onLogOutPress}
              >
                {t("sidebar.settings.logout")}
              </Navigation.Button>
            </Navigation.DropdownMenu>
          </>
        ) : null}
      </Navigation>

      {isCustomerView && (
        <View flex={1} justifyContent="flex-end">
          <Image
            source={{ uri: homeServiceLogoImage, width: 189, height: 72 }}
            width="100%"
            height={72}
            resizeMode="stretch"
            alt={t("sidebar.homeServiceLogoAlt")}
            alignSelf="center"
          />
        </View>
      )}
    </YStack>
  );
};
